import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import channelsHelper from "../../../../API/Channels/channelsHelper";
import secretGenerator from "../../../../API/SecretGenerator";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import ChannelSettingsAIView from "../SubComponents/ChannelSettingsAIView";

const ChannelsSettingsAI = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onChannelCreated,
      selectedId,
      setShowHostingModal,
      onChannelDeleted = () => {},
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
    },
    ref
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();
    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace } = useWorkspaces();
    const [isSaveInProgress, setIsSaveInProgress] = React.useState(false);
    const [isUpdatingSecret, setIsUpdatingSecret] = React.useState(false);
    const validationSchema = AppForms.yup.object().shape({
      channel: AppForms.yup.string().required().label("Channel"),
      name: AppForms.yup
        .string()
        .required()
        .label("Name")
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces."
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup.string().label("Description").max(255),
      tokenSecret: AppForms.yup.string().when("provider", {
        is: (provider) => provider != "ses",
        then: AppForms.yup.string().required().label("API Key"),
        otherwise: AppForms.yup.string().notRequired().nullable(),
      }),
      senderid: AppForms.yup.string().when("channel", {
        is: (channel) => channel != "email" && channel != "telegram",
        then: AppForms.yup.string().required().label("Client ID"),
        otherwise: AppForms.yup.string().notRequired(),
      }),
    });

    const handleDeletion = async () => {
      try {
        // SIP trunk is now managed at the agent level, not deleted with channel
        const result = await channelsHelper.deleteChannel(opt.id);
        dispatch("SET_CHANNELS", [
          ...state?.channels?.filter((u) => u?.id !== opt.id),
        ]);
        setShowHostingModal?.(false);
        onChannelDeleted();
        AppToasty.show(t("toasts.channelDeleted"), {
          type: "success",
        });
      } catch (error) {
        AppToasty.show(t("toasts.channelDeletionFailed"), {
          type: "danger",
        });
      }
    };
    const navigateBack = () => {
      setIsUpdatingSecret(false);
      setDisplayedView && setDisplayedView(backTo);
    };
    const handleUpdateSecret = () => {
      setIsUpdatingSecret(true);
    };
    const identifyOptByID = (id) => {
      return state?.channels.find((u) => u?.id === id);
    };
    const handleChannelItemSave = async (submitted) => {
      let v = { ...submitted };
      setIsSaveInProgress(true);
      v.workspaceID = getActiveWorkspace()?.id;
      if (v?.config) v.config = JSON.stringify(v.config, null, 2);
      delete v?._lastChangedAt;
      delete v?.createdAt;
      delete v?.updatedAt;
      delete v?._deleted;
      delete v?.__typename;
      const psw = v?.type == "oauth" ? v?.clientSecret : v?.tokenSecret;
      delete v?.tokenSecret;
      let newAuthorisations = [...state?.channels];
      try {
        let result;
        if (isEdit) {
          result = await channelsHelper.updateChannel(opt.id, v);
          const _updatedObject = identifyOptByID(
            result.data?.updateCommunicationService?.id
          );
          const updateIndex = newAuthorisations.findIndex(
            (u) => u?.id === _updatedObject?.id
          );
          newAuthorisations[updateIndex] =
            result?.data?.updateCommunicationService;

          if (psw && isUpdatingSecret && v?.provider !== "ses") {
            const secretResult = await secretGenerator.updateSecret({
              workspaceid: v.workspaceID,
              id: opt.id,
              passwordSecretValue: psw,
            });
            if (secretResult?.data?.status !== "Success") {
              throw new Error("Secret could not be updated");
            }
          }
        } else {
          if (state?.channels?.find((u) => u?.name === v?.name)) {
            AppToasty.show(t("toasts.channelNameExists"), {
              type: "danger",
            });
            setIsSaveInProgress(false);
            return;
          }
          if (
            state?.channels?.find(
              (u) => u?.channel === v?.channel && u?.provider == v?.provider
            )
          ) {
            AppToasty.show(
              "The combination of channel type and provider selected already exists",
              {
                type: "danger",
              }
            );
            setIsSaveInProgress(false);
            return;
          }
          result = await channelsHelper.createChannel({
            ...v,
          });
          const _id = result?.data?.createCommunicationService?.id;
          if (v?.type != "local" && v?.type != "aws" && v?.provider != "ses") {
            const secretResult = await secretGenerator.storeSecret({
              workspaceid: v.workspaceID,
              id: _id,
              passwordSecretValue: psw,
            });
            if (secretResult?.data?.status === "Success") {
              const payload =
                v?.type == "oauth"
                  ? {
                      clientSecret: secretResult?.data?.name,
                    }
                  : {
                      tokenSecret: secretResult?.data?.name,
                    };
              await channelsHelper.updateChannel(_id, payload);
            } else {
              throw new Error("Secret could not be created");
            }
          }
          // SIP trunk is now created when agent is saved with a phone number
          // No need to create trunk here - just save Twilio credentials

          // For SIP channels, just save the Twilio credentials
          // Outbound trunk will be created when an agent is assigned a phone number
          if (v?.channel === "sip" && v?.senderid && psw) {
            // Store Twilio credentials in channel config for later use
            const existingConfig = v.config ? JSON.parse(v.config) : {};
            const updatedConfig = {
              ...existingConfig,
              twilio_sid: v.senderid,
              // Token is stored in Secrets Manager, we just note it exists
              has_twilio_token: true,
            };
            await channelsHelper.updateChannel(_id, {
              config: JSON.stringify(updatedConfig),
            });
          }

          const updatedChannel = await channelsHelper.getChannel(_id);
          newAuthorisations.push(updatedChannel?.data?.getCommunicationService);
        }

        dispatch("SET_CHANNELS", [...newAuthorisations]);
        setIsUpdatingSecret(false);

        // For SIP channels, show success toast
        if (v?.channel === "sip") {
          AppToasty.show(t("toasts.sipTrunkCreated"), {
            type: "success",
          });
        }

        backTo && navigateBack();
        const savedId =
          result?.data?.updateCommunicationService?.id ||
          result?.data?.createCommunicationService?.id;
        onChannelCreated && onChannelCreated("success", savedId, submitted);
      } catch (error) {
        console.error(error);
        setIsUpdatingSecret(false);
        AppToasty.show(
          t("toasts.channelSaveFailed", {
            error: error?.message || t("toasts.unknownError"),
          }),
          {
            type: "danger",
          }
        );
        onChannelCreated && onChannelCreated("error", null, submitted);
      } finally {
        setIsSaveInProgress(false);
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  paddingHorizontal: 24,
                  paddingTop: 20,
                  paddingBottom: 40,
                  minWidth: 300,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                  height: "auto",
                }
          }
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={"Channel : " + sectionTitle}
              isClickable
              isBackIconDisplayed
            />
          )}

          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleChannelItemSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <ChannelSettingsAIView
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
              isSaveInProgress={isSaveInProgress}
              onUpdateSecret={handleUpdateSecret}
              isUpdatingSecret={isUpdatingSecret}
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state.activeWorkspaceID,
        initialMapValues,
        state?.channels,
        isEdit,
        AppToasty,
        isSaveInProgress,
        isUpdatingSecret,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
      ]
    );
    return _view;
  }
);

export default ChannelsSettingsAI;
